import React, { useState, useContext, useCallback } from "react";
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Badge,
  Menu,
  MenuItem,
  ListItemIcon,
  Box,
  Popover,
  List,
  ListItemButton,
  Divider,
  Avatar,
  ListItemText,
  Button,
  Tooltip,
  LinearProgress,
  Collapse,
} from "@mui/material";
import {
  Menu as MenuIcon,
  X,
  Bell,
  User,
  Lock,
  LogOut,
  LayoutGrid,
  Download,
  BellOff,
  Users,
  ChevronDown,
  ChevronUp,
  UserCircle,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import useSiteTitleDesc from "../../hooks/useSiteTitleDesc";
import { UserContext } from "../../context/UserProvider";
import useAdminEventSettings from "../../hooks/useAdminEventSettings";
import { API_ENDPOINT, USER_ROLES } from "../../utils/constants";
import axios from "axios";
import { toastError } from "../../utils/util";
import { getHeader, saveToken } from "../../utils/utils2";

export const AppBarHeader = ({ open, handleDrawerOpen }) => {
  const { data, isLoading, isError } = useAdminEventSettings();
  const { user, loading } = useContext(UserContext);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const [switchingRole, setSwitchingRole] = useState(false);
  const [anchorNotificationEl, setAnchorNotificationEl] = useState(null);
  const [isSwitchRoleOpen, setIsSwitchRoleOpen] = useState(false);

  const isMenuOpen = Boolean(anchorEl);
  const isNotificationOpen = Boolean(anchorNotificationEl);

  const handleProfileMenuOpen = (event) => setAnchorEl(event.currentTarget);
  const handleMenuClose = () => {
    setAnchorEl(null);
    setIsSwitchRoleOpen(false);
  };

  const handleNotificationOpen = (event) => setAnchorNotificationEl(event.currentTarget);
  const handleNotificationClose = () => setAnchorNotificationEl(null);

  const getInitials = (name) => {
    if (!name) return "EP";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .substring(0, 2)
      .toUpperCase();
  };

  const notificationCount = 3;
  const sampleNotifications = [
    { id: 1, text: "New Visitor Registered", description: "Rahul Sharma registered for PMTX 2026", link: "/visitors-list" },
    { id: 2, text: "Trade Delegate Pending", description: "3 delegates waiting for approval", link: "/manage-delegates" },
    { id: 3, text: "OES Submission Updated", description: "Stall #B12 submitted booth design", link: "/oes-forms" },
  ];

  const handleNotificationClick = (link) => {
    handleNotificationClose();
    navigate(link);
  };

  const switchRoles = [
    { label: "Super Admin", value: USER_ROLES.SUPER || "super" },
    { label: "Admin", value: USER_ROLES.ADMIN || "admin" },
    { label: "OES", value: USER_ROLES.OES || "oes" },
    { label: "TD Admin", value: USER_ROLES.TD_ADMIN || "TD Admin" },
  ];

  const handleSwitchRoleToggle = (e) => {
    e.stopPropagation();
    setIsSwitchRoleOpen(!isSwitchRoleOpen);
  };

  const handleSwitchRole = useCallback(async (targetRole) => {
    handleMenuClose();
    try {
      setSwitchingRole(true);
      const response = await axios.post(
        `${API_ENDPOINT}/user/switch-user`,
        { targetUserRole: targetRole },
        getHeader()
      );
      saveToken(response.data.data.token);
      document.location.reload();
    } catch (error) {
      toastError(error);
    } finally {
      setSwitchingRole(false);
    }
  }, []);

  if (loading) {
    return <LinearProgress />;
  }

  return (
    <AppBar
      position="fixed"
      elevation={0}
      sx={{
        zIndex: (theme) => theme.zIndex.drawer + 1,
        backgroundColor: "#FFFFFF",
        color: "#172033",
        borderBottom: "1px solid #E7EAF0",
        height: 70,
        justifyContent: "center",
      }}
    >
      <Toolbar sx={{ justifyContent: "space-between", px: { xs: 2, sm: 3 } }}>
        {/* Left Section: Menu Toggle, Logo & Title */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          <Tooltip title={open ? "Close navigation" : "Open navigation"}>
            <IconButton
              onClick={handleDrawerOpen}
              edge="start"
              sx={{
                color: "#475467",
                transition: "all 0.2s",
                p: 1,
                "&:hover": { backgroundColor: "#F1F5F9" },
              }}
            >
              {open ? <X size={22} /> : <MenuIcon size={22} />}
            </IconButton>
          </Tooltip>

          <Box
            onClick={() => navigate("/")}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
            }}
          >
            {data?.eventLogo ? (
              <Box
                component="img"
                src={data.eventLogo}
                alt="Event Logo"
                sx={{
                  height: 38,
                  width: "auto",
                  objectFit: "contain",
                }}
              />
            ) : (
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  borderRadius: "10px",
                  bgcolor: "#2563EB",
                  color: "#FFFFFF",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontWeight: 700,
                  fontSize: 16,
                }}
              >
                E
              </Box>
            )}
            <Typography
              variant="h6"
              noWrap
              sx={{
                fontWeight: 700,
                fontSize: { xs: "1.1rem", sm: "1.25rem" },
                color: "#1E293B",
                letterSpacing: "-0.02em",
              }}
            >
              {data?.siteTitle || "PMTX 2026"}
            </Typography>
          </Box>
        </Box>

        {/* Right Section: Notifications & Profile Dropdown */}
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          {/* Notifications Icon */}
          <Tooltip title="Notifications">
            <IconButton
              onClick={handleNotificationOpen}
              sx={{
                color: "#64748B",
                backgroundColor: "#F8FAFC",
                border: "1px solid #E2E8F0",
                p: 1,
                borderRadius: "10px",
                "&:hover": { backgroundColor: "#F1F5F9", color: "#1E293B" },
              }}
            >
              <Badge
                badgeContent={notificationCount}
                color="error"
                sx={{
                  "& .MuiBadge-badge": {
                    backgroundColor: "#EF4444",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: "0.75rem",
                  },
                }}
              >
                <Bell size={20} />
              </Badge>
            </IconButton>
          </Tooltip>

          <Popover
            open={isNotificationOpen}
            anchorEl={anchorNotificationEl}
            onClose={handleNotificationClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 1.5 }}
            PaperProps={{
              sx: {
                width: 360,
                borderRadius: "14px",
                boxShadow: "0px 10px 30px rgba(16, 24, 40, 0.1)",
                border: "1px solid #E7EAF0",
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                p: 2,
                pb: 1.5,
              }}
            >
              <Typography variant="subtitle1" fontWeight="700" color="#1E293B">
                Notifications
              </Typography>
              <Button size="small" sx={{ textTransform: "none", fontSize: "0.8rem" }}>
                Clear all
              </Button>
            </Box>
            <Divider />

            <List sx={{ p: 0, maxHeight: 360, overflowY: "auto" }}>
              {sampleNotifications.map((n, index) => (
                <React.Fragment key={n.id}>
                  <ListItemButton
                    onClick={() => handleNotificationClick(n.link)}
                    sx={{ p: 2, alignItems: "flex-start" }}
                  >
                    <ListItemText
                      primary={n.text}
                      secondary={n.description}
                      primaryTypographyProps={{
                        variant: "subtitle2",
                        fontWeight: 600,
                        color: "#1E293B",
                      }}
                      secondaryTypographyProps={{ variant: "body2", color: "#64748B", mt: 0.5 }}
                    />
                  </ListItemButton>
                  {index < sampleNotifications.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          </Popover>

          {/* User Profile Pill */}
          <Box
            onClick={handleProfileMenuOpen}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 1.5,
              cursor: "pointer",
              p: 0.5,
              pl: 1,
              pr: 1.5,
              borderRadius: "12px",
              backgroundColor: "#F8FAFC",
              border: "1px solid #E2E8F0",
              transition: "all 0.2s",
              "&:hover": { backgroundColor: "#F1F5F9", borderColor: "#CBD5E1" },
            }}
          >
            <Avatar
              src={user?.profilePic}
              alt={user?.name || "ExpoPlanner Admin"}
              sx={{
                width: 36,
                height: 36,
                bgcolor: "#2563EB",
                fontWeight: 700,
                fontSize: 14,
              }}
            >
              {getInitials(user?.name)}
            </Avatar>
            <Box sx={{ display: { xs: "none", sm: "block" }, textAlign: "left" }}>
              <Typography
                variant="subtitle2"
                fontWeight="700"
                sx={{ color: "#1E293B", lineHeight: 1.2, fontSize: "0.875rem" }}
              >
                {user?.name || "Expoplanner"}
              </Typography>
              <Typography
                variant="caption"
                sx={{ color: "#64748B", fontWeight: 500, fontSize: "0.75rem" }}
              >
                {user?.role ? `${user.role.toUpperCase()} Admin` : "Super Admin"}
              </Typography>
            </Box>
            <ChevronDown size={16} style={{ color: "#64748B" }} />
          </Box>

          {/* Profile Menu */}
          <Menu
            anchorEl={anchorEl}
            open={isMenuOpen}
            onClose={handleMenuClose}
            anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
            transformOrigin={{ vertical: "top", horizontal: "right" }}
            sx={{ mt: 1.5 }}
            PaperProps={{
              sx: {
                minWidth: 260,
                borderRadius: "14px",
                boxShadow: "0px 10px 30px rgba(16, 24, 40, 0.1)",
                border: "1px solid #E7EAF0",
                p: 0.5,
              },
            }}
          >
            <Box sx={{ px: 2, py: 1.5 }}>
              <Typography variant="subtitle1" fontWeight="700" color="#1E293B">
                {user?.name || "Expoplanner"}
              </Typography>
              <Typography variant="body2" color="#64748B" noWrap>
                {user?.email || "admin@expoplanner.in"}
              </Typography>
            </Box>
            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/profile");
              }}
              sx={{ borderRadius: "8px" }}
            >
              <ListItemIcon>
                <User size={18} color="#475467" />
              </ListItemIcon>
              <ListItemText primary="Profile Settings" />
            </MenuItem>

            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/change-password");
              }}
              sx={{ borderRadius: "8px" }}
            >
              <ListItemIcon>
                <Lock size={18} color="#475467" />
              </ListItemIcon>
              <ListItemText primary="Change Password" />
            </MenuItem>

            {/* <MenuItem
              onClick={(e) => {
                e.stopPropagation();
                setIsSwitchRoleOpen((prev) => !prev);
              }}
              sx={{
                borderRadius: "8px",
                display: "flex",
                justify: "space-between",
                alignItems: "center",
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center" }}>
                <ListItemIcon sx={{ minWidth: "auto", mr: 1.5 }}>
                  <Users size={18} color="#475467" />
                </ListItemIcon>
                <ListItemText primary="Switch User" />
              </Box>
              {isSwitchRoleOpen ? (
                <ChevronUp size={16} color="#475467" style={{ marginLeft: 8 }} />
              ) : (
                <ChevronDown size={16} color="#475467" style={{ marginLeft: 8 }} />
              )}
            </MenuItem> */}

            <Collapse in={isSwitchRoleOpen} timeout="auto" unmountOnExit>
              <Box sx={{ pl: 1, pr: 1, py: 0.5, bgcolor: "#F8FAFC", borderRadius: "8px", my: 0.5 }}>
                {switchRoles.map((roleItem) => {
                  const isActive =
                    user?.role === roleItem.value ||
                    (!user?.role && roleItem.value === (USER_ROLES.SUPER || "super"));
                  return (
                    <MenuItem
                      key={roleItem.value}
                      disabled={switchingRole}
                      onClick={() => handleSwitchRole(roleItem.value)}
                      sx={{
                        borderRadius: "6px",
                        py: 0.75,
                        px: 1.5,
                        fontSize: "0.85rem",
                        fontWeight: isActive ? 700 : 500,
                        color: isActive ? "#2563EB" : "#334155",
                        display: "flex",
                        alignItems: "center",
                        justify: "space-between",
                        "&:hover": { bgcolor: "#E2E8F0" },
                      }}
                    >
                      <Box sx={{ display: "flex", alignItems: "center" }}>
                        <UserCircle
                          size={15}
                          style={{
                            marginRight: 8,
                            color: isActive ? "#2563EB" : "#64748B",
                          }}
                        />
                        <span>{roleItem.label}</span>
                      </Box>
                      {isActive && (
                        <Typography
                          variant="caption"
                          sx={{
                            fontSize: "0.7rem",
                            color: "#2563EB",
                            fontWeight: 700,
                            ml: 1,
                          }}
                        >
                          Active
                        </Typography>
                      )}
                    </MenuItem>
                  );
                })}
              </Box>
            </Collapse>

            <Divider sx={{ my: 0.5 }} />

            <MenuItem
              onClick={() => {
                handleMenuClose();
                navigate("/logout");
              }}
              sx={{ borderRadius: "8px", color: "#DC2626" }}
            >
              <ListItemIcon>
                <LogOut size={18} color="#DC2626" />
              </ListItemIcon>
              <ListItemText primary="Logout" />
            </MenuItem>
          </Menu>
        </Box>
      </Toolbar>
    </AppBar>
  );
};
